<template>
  <NavBar pageName="SIMs"></NavBar>
  <v-container style="margin-top: 185px;">
    <div class="d-flex align-center mb-4">
      <h2 class="text-h4">SIMs Management</h2>
      <v-tooltip text="Rebuild client hierarchy cache" location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon="mdi-database-refresh" variant="text" size="small" class="ml-2" :loading="cacheRebuilding" @click="rebuildCache" />
        </template>
      </v-tooltip>
    </div>
    <!-- Filters Card -->
    <v-card class="mb-4 pa-4">
      <v-card-title>Filters</v-card-title>
      <v-card-text>
        <SimSearchFilters
          v-model:search-term="searchTerm"
          v-model:client-id="selectedClientId"
          v-model:include-subclients="includeSubclients"
          v-model:status-id="selectedStatusId"
          search-label="Search by IMSI, Serial, MSISDN, Seria Echipament, NUI, Technician"
          search-button-text="Search"
          :use-chips="false"
          :always-allow-search="!isDistributor"
          @search="search"
          @reset="resetFilters"
          @hierarchy-loaded="onHierarchyLoaded"
        />
      </v-card-text>
    </v-card>

    <!-- Search Hint (when no search done yet) -->
    <v-alert v-if="!initialLoadDone && !loading" type="info" variant="tonal" class="mb-4">
      <v-icon class="mr-2">mdi-information</v-icon>
      Introduceti minim 3 caractere in campul de cautare, selectati un client, sau alegeti un status specific pentru a afisa rezultate.
      Puteti folosi <strong>*</strong> ca wildcard (ex: <strong>alfa*srl</strong>).
    </v-alert>

    <!-- Results Summary -->
    <v-row v-if="initialLoadDone" class="mb-2">
      <v-col cols="12">
        <span class="text-caption">
          Showing {{ sims.length }} of {{ totalCount }} results
        </span>
      </v-col>
    </v-row>

    <!-- Results Card -->
    <v-card v-if="initialLoadDone || loading">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>SIMs: {{ totalCount }}</span>
      </v-card-title>
      <v-card-text>
        <v-data-table
            v-model:expanded="expanded"
            :headers="headers"
            :items="sims"
            :loading="loading"
            show-expand
            item-value="id"
            :items-per-page="perPage"
            hide-default-footer
            :row-props="getRowProps"
            v-model:sort-by="sortBy"
            @update:sort-by="onSortChange"
        >
          <!-- Serial column -->
          <template v-slot:[`item.serial`]="{ item }">
            <v-tooltip location="right" v-if="item.serial">
              <template v-slot:activator="{ props }">
                <span v-bind="props" class="clickable-link" @click.stop="navigateToImsiList(item.serial)">
                  {{ item.serial }}
                </span>
              </template>
              <div class="pa-2">
                <div class="font-weight-bold mb-2">SIM Info</div>
                <div><strong>SIM ID:</strong> {{ item.id }}</div>
                <div><strong>IMSI:</strong> {{ item.imsi_number || '-' }}</div>
                <div><strong>MSISDN:</strong> {{ item.msisdn || '-' }}</div>
                <div><strong>Status:</strong> {{ formatStatusLabel(item.sim_status_name) }}</div>
                <div><strong>Client:</strong> {{ item.client_name || '-' }}</div>
                <div><strong>Service:</strong> {{ item.service_name || '-' }}</div>
                <div><strong>CS Status:</strong> {{ item.client_service_status || '-' }}</div>
                <div><strong>Expiration:</strong> {{ formatDate(item.client_service_expiration_date) }}</div>
                <div><strong>SIM Created:</strong> {{ formatDateTime(item.created_at) }}</div>
                <div><strong>SIM Updated:</strong> {{ formatDateTime(item.updated_at) }}</div>
              </div>
            </v-tooltip>
            <span v-else>-</span>
          </template>

          <!-- MSISDN column -->
          <template v-slot:[`item.msisdn`]="{ item }">
            <span v-if="item.msisdn && item.msisdn_id" class="clickable-link" @click.stop="navigateToMsisdnList(item.msisdn_id)">
              {{ item.msisdn }}
            </span>
            <span v-else>-</span>
          </template>

          <!-- Status column with chip -->
          <template v-slot:[`item.sim_status_name`]="{ item }">
            <v-chip size="small" :color="getStatusColor(item.sim_status_name)">
              {{ formatStatusLabel(item.sim_status_name) }}
            </v-chip>
          </template>

          <!-- Client Name column -->
          <template v-slot:[`item.client_name`]="{ item }">
            <span class="clickable-link" @click.stop="navigateToClient(item.client_id)">
              {{ item.client_name }}
            </span>
          </template>

          <!-- Services column (link to ClientServicesList filtered by sim) -->
          <template v-slot:[`item.services_count`]="{ item }">
            <v-chip
                v-if="item.services_count > 0"
                size="small"
                color="primary"
                class="clickable-link"
                @click.stop="navigateToSimServices(item.id)"
            >
              <v-icon start size="small">mdi-format-list-bulleted</v-icon>
              {{ item.services_count }} {{ item.services_count === 1 ? 'serviciu' : 'servicii' }}
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- Expiration Date column -->
          <template v-slot:[`item.expiration_date`]="{ item }">
            <span :class="{'text-red': isExpiringSoon(item.effective_expiration_date || item.client_service_expiration_date)}">
              {{ formatDate(item.client_service_expiration_date) }}
            </span>
            <div v-if="item.effective_expiration_date && item.effective_expiration_date !== item.client_service_expiration_date" class="text-caption text-teal-darken-2">
              <v-icon size="x-small" class="mr-1">mdi-calendar-arrow-right</v-icon>
              Eff: {{ formatDate(item.effective_expiration_date) }}
            </div>
          </template>

          <!-- Actions column -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <!-- Info button - always visible -->
              <v-btn
                  size="x-small"
                  color="success"
                  variant="elevated"
                  class="mr-2"
                  @click.stop="navigateToSimDetails(item.id)"
              >
                <v-icon size="small" class="mr-1">mdi-eye</v-icon>
                Info
              </v-btn>

              <!-- More Actions dropdown -->
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                      size="x-small"
                      color="grey-darken-1"
                      v-bind="props"
                  >
                    More Actions
                    <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-subheader>More Actions</v-list-subheader>

                  <!-- not_provisioned: Provision -->
                  <v-list-item
                      v-if="item.sim_status_name === 'not_provisioned'"
                      @click="openProvisionDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary" size="small">mdi-play-circle</v-icon>
                    </template>
                    <v-list-item-title>Provision</v-list-item-title>
                  </v-list-item>

                  <!-- not_provisioned: Add to Inventory -->
                  <v-list-item
                      v-if="item.sim_status_name === 'not_provisioned'"
                      @click="openInventoryDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="teal" size="small">mdi-package-variant</v-icon>
                    </template>
                    <v-list-item-title>Add to Inventory</v-list-item-title>
                  </v-list-item>

                  <!-- pending: Refresh -->
                  <v-list-item
                      v-if="item.sim_status_name === 'pending'"
                      @click="confirmRefresh(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="info" size="small">mdi-refresh</v-icon>
                    </template>
                    <v-list-item-title>Refresh</v-list-item-title>
                  </v-list-item>

                  <!-- active: Edit -->
                  <v-list-item
                      v-if="item.sim_status_name === 'active'"
                      @click="openEditDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary" size="small">mdi-pencil</v-icon>
                    </template>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <!-- active: Add Client Service -->
                  <v-list-item
                      v-if="item.sim_status_name === 'active'"
                      @click="openAddClientServiceDialog(item)"
                      :loading="addClientServiceLoading"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success" size="small">mdi-plus-circle</v-icon>
                    </template>
                    <v-list-item-title>Adauga ClientService</v-list-item-title>
                  </v-list-item>

                  <!-- active: Change MSISDN -->
                  <v-list-item
                      v-if="item.sim_status_name === 'active'"
                      @click="openChangeMsisdnDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="secondary" size="small">mdi-swap-horizontal</v-icon>
                    </template>
                    <v-list-item-title>Change MSISDN</v-list-item-title>
                  </v-list-item>

                  <!-- active: Suspend -->
                  <v-list-item
                      v-if="item.sim_status_name === 'active'"
                      @click="confirmSuspend(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="orange" size="small">mdi-pause-circle</v-icon>
                    </template>
                    <v-list-item-title>Suspend</v-list-item-title>
                  </v-list-item>

                  <!-- active: Terminate -->
                  <v-list-item
                      v-if="item.sim_status_name === 'active'"
                      @click="confirmTerminate(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="error" size="small">mdi-stop-circle</v-icon>
                    </template>
                    <v-list-item-title>Terminate</v-list-item-title>
                  </v-list-item>

                  <!-- suspended: Reactivate -->
                  <v-list-item
                      v-if="item.sim_status_name === 'suspended'"
                      @click="confirmReactivate(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success" size="small">mdi-play-circle</v-icon>
                    </template>
                    <v-list-item-title>Reactivate</v-list-item-title>
                  </v-list-item>

                  <!-- terminated: Provision (reprovision) -->
                  <v-list-item
                      v-if="item.sim_status_name === 'terminated'"
                      @click="openReprovisionDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary" size="small">mdi-play-circle</v-icon>
                    </template>
                    <v-list-item-title>Provision</v-list-item-title>
                  </v-list-item>

                  <!-- terminated: Renew -->
                  <v-list-item
                      v-if="item.sim_status_name === 'terminated'"
                      @click="openRenewDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success" size="small">mdi-refresh</v-icon>
                    </template>
                    <v-list-item-title>Renew</v-list-item-title>
                  </v-list-item>

                  <!-- Traffic - visible when SIM has subscriber_id (provisioned at MVNO) -->
                  <v-list-item
                      v-if="item.subscriber_id"
                      @click="navigateToTraffic(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="deep-purple" size="small">mdi-chart-timeline-variant</v-icon>
                    </template>
                    <v-list-item-title>Trafic</v-list-item-title>
                  </v-list-item>

                  <!-- Network Services (Roaming & International) - visible when SIM is active and has subscriber_id -->
                  <v-list-item
                      v-if="item.sim_status_name === 'active' && item.subscriber_id"
                      @click="navigateToNetworkServices(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="deep-orange" size="small">mdi-radio-tower</v-icon>
                    </template>
                    <v-list-item-title>Servicii Retea</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>

          <template v-slot:[`item.data-table-expand`]="{ toggleExpand, isExpanded, internalItem }">
            <v-btn icon variant="text" size="small" @click="toggleExpand(internalItem)">
              <v-icon :color="isExpanded(internalItem) ? 'primary' : ''">mdi-eye{{ isExpanded(internalItem) ? '' : '-off-outline' }}</v-icon>
            </v-btn>
          </template>

          <!-- Expanded Row -->
          <template v-slot:expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length" class="pa-4 bg-grey-lighten-4">
                <v-row>
                  <v-col cols="12" md="4">
                    <h4 class="mb-2">Technical Info</h4>
                    <div><strong>SIM ID:</strong> {{ item.id }}</div>
                    <div><strong>Subscriber ID:</strong> {{ item.subscriber_id || '-' }}</div>
                    <div><strong>SubscriberID:</strong> {{ item.subscriberID || '-' }}</div>
                    <div><strong>Provisioning MSISDN ID:</strong> {{ item.provisioning_msisdn_id || '-' }}</div>
                    <div><strong>Provisioning IMSI ID:</strong> {{ item.provisioning_imsi_id || '-' }}</div>
                    <div><strong>Description:</strong> {{ item.description || '-' }}</div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <h4 class="mb-2">Client Service</h4>
                    <div>
                      <strong>ID:</strong>
                      <span class="clickable-link" @click="navigateToClientService(item.client_service_id)">
                        {{ item.client_service_id }}
                      </span>
                    </div>
                    <div><strong>Description:</strong> {{ item.client_service_description || '-' }}</div>
                    <div><strong>Status:</strong> {{ item.client_service_status || '-' }}</div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <h4 class="mb-2">Order Info</h4>
                    <div v-if="item.order_id">
                      <div>
                        <strong>Order ID:</strong>
                        <span class="clickable-link" @click="navigateToOrder(item.order_id)">
                          {{ item.order_id }}
                        </span>
                      </div>
                      <div><strong>Order Status:</strong> {{ item.order_status_name || '-' }}</div>
                      <div><strong>Delivery Status:</strong> {{ item.order_delivery_status || '-' }}</div>
                    </div>
                    <div v-else>No order associated</div>

                    <h4 class="mb-2 mt-4">Links</h4>
                    <div v-if="item.imsi_id">
                      <strong>IMSI:</strong>
                      <span class="clickable-link" @click="navigateToImsi(item.imsi_id)">
                        {{ item.imsi_number }}
                      </span>
                    </div>
                    <div v-if="item.msisdn_id">
                      <strong>MSISDN:</strong>
                      <span class="clickable-link" @click="navigateToMsisdn(item.msisdn_id)">
                        {{ item.msisdn }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
                <!-- Update Info Button and Message -->
                <v-row class="mt-3">
                  <v-col cols="12">
                    <v-btn
                      v-if="item.imsi_id"
                      color="primary"
                      variant="outlined"
                      size="small"
                      :loading="updatingImsiInfo && updateImsiInfoItemId === item.id"
                      @click="updateSimSubscriberInfo(item)"
                    >
                      <v-icon class="mr-1">mdi-refresh</v-icon>
                      Update mobile network info
                    </v-btn>
                    <v-alert
                      v-if="updateImsiInfoItemId === item.id && updateImsiInfoMessage"
                      :type="updateImsiInfoSuccess ? 'success' : 'error'"
                      density="compact"
                      class="mt-2"
                      closable
                      @click:close="updateImsiInfoMessage = ''"
                    >
                      {{ updateImsiInfoMessage }}
                    </v-alert>
                  </v-col>
                </v-row>
              </td>
            </tr>
          </template>
        </v-data-table>

        <!-- Pagination -->
        <v-pagination
            v-if="totalCount > perPage"
            v-model="page"
            :length="totalPages"
            @update:model-value="onPageChange"
            class="mt-4"
        ></v-pagination>
      </v-card-text>
    </v-card>

    <!-- Action Confirmation Dialog -->
    <v-dialog v-model="actionDialog" max-width="400">
      <v-card>
        <v-card-title>{{ actionDialogTitle }}</v-card-title>
        <v-card-text>
          <span>{{ actionDialogMessage }}</span>
          <div v-if="actionItem" class="mt-2">
            <strong>SIM ID:</strong> {{ actionItem.id }}<br>
            <strong>Serial:</strong> {{ actionItem.serial || '-' }}<br>
            <strong>MSISDN:</strong> {{ actionItem.msisdn || '-' }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="actionDialog = false">Cancel</v-btn>
          <v-btn :color="actionDialogButtonColor" @click="executeAction" :loading="actionLoading">
            {{ actionDialogButtonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Provision Dialog -->
    <v-dialog v-model="provisionDialog" max-width="600">
      <v-card>
        <v-card-title>Provision SIM</v-card-title>
        <v-card-text>
          <div v-if="provisionItem" class="mb-4">
            <strong>SIM ID:</strong> {{ provisionItem.id }}<br>
            <strong>Serial:</strong> {{ provisionItem.serial || '-' }}<br>
            <strong>Client:</strong> {{ provisionItem.client_name || '-' }}<br>
            <span v-if="provisionItem.msisdn_id">
              <strong>MSISDN:</strong> {{ provisionItem.msisdn }} (already assigned)
            </span>
            <span v-if="provisionItem.imsi_id">
              <br><strong>IMSI:</strong> {{ provisionItem.imsi_number || provisionItem.serial }} (already assigned)
            </span>
          </div>

          <!-- MSISDN Selection Section -->
          <template v-if="needsMsisdnSelection">
            <div class="text-subtitle-1 font-weight-bold mb-2">1. Select MSISDN</div>
            <v-row class="mb-2">
              <v-col cols="12" md="5">
                <v-text-field
                    v-model="msisdnLike"
                    label="MSISDN pattern"
                    placeholder="e.g. 0788"
                    density="compact"
                    clearable
                    @keyup.enter="searchMsisdns"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-select
                    v-model="selectedMsisdnClassId"
                    :items="msisdnClasses"
                    item-title="name"
                    item-value="id"
                    label="MSISDN Class"
                    density="compact"
                    @update:model-value="searchMsisdns"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn color="primary" size="small" @click="searchMsisdns" :loading="msisdnSearchLoading">
                  Search
                </v-btn>
              </v-col>
            </v-row>

            <v-autocomplete
                v-model="selectedMsisdnForProvision"
                :items="availableMsisdns"
                item-title="msisdn"
                item-value="msisdn_id"
                label="Select MSISDN"
                hint="Choose an available MSISDN for this SIM"
                persistent-hint
                :loading="msisdnSearchLoading"
                class="mb-4"
            ></v-autocomplete>
          </template>

          <!-- MSISDN already assigned or auto-assign message -->
          <template v-else-if="!provisionItem?.msisdn_id && !hasSelectMsisdnRole">
            <v-alert type="info" variant="tonal" class="mb-4">
              An MSISDN will be automatically assigned from the standard class.
            </v-alert>
          </template>

          <!-- Product Code Selection Section -->
          <div class="text-subtitle-1 font-weight-bold mb-2">{{ needsMsisdnSelection ? '2.' : '1.' }} Select Product Code</div>
          <v-select
              v-model="selectedProductForProvision"
              :items="productCodesWithDescription"
              item-title="display"
              item-value="name"
              label="Select Product Code"
              hint="Choose the MNO product code for provisioning"
              persistent-hint
              :loading="productsLoading"
              class="mb-4"
              :rules="[v => !!v || 'Product code is required']"
          ></v-select>

          <!-- IMSI Selection Section -->
          <template v-if="needsImsiSelection">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ needsMsisdnSelection ? '3.' : '2.' }} Select IMSI</div>

            <!-- No IMSIs available alert -->
            <v-alert v-if="noImsisAvailable" type="warning" variant="tonal" class="mb-4">
              <strong>No IMSIs available for this client.</strong><br>
              Please allocate IMSIs to the client before provisioning.
            </v-alert>

            <template v-else>
              <v-row class="mb-2">
                <v-col cols="12" md="8">
                  <v-text-field
                      v-model="imsiSearchTerm"
                      label="Search IMSI/Serial"
                      placeholder="Enter IMSI or serial number"
                      density="compact"
                      clearable
                      @keyup.enter="searchImsis"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn color="primary" size="small" @click="searchImsis" :loading="imsiSearchLoading">
                    Search
                  </v-btn>
                </v-col>
              </v-row>

              <v-autocomplete
                  v-model="selectedImsiForProvision"
                  :items="availableImsis"
                  item-title="display_name"
                  item-value="id"
                  label="Select IMSI"
                  hint="Choose an available IMSI allocated to this client"
                  persistent-hint
                  :loading="imsiSearchLoading"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template v-slot:subtitle>
                      Serial: {{ item.raw.serial }}
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </template>
          </template>

        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="provisionDialog = false">Cancel</v-btn>
          <v-btn
              color="primary"
              @click="executeProvision"
              :loading="provisionLoading"
              :disabled="!selectedProductForProvision || (needsMsisdnSelection && !selectedMsisdnForProvision) || (needsImsiSelection && !selectedImsiForProvision) || noImsisAvailable"
          >
            Provision
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change MSISDN Dialog -->
    <v-dialog v-model="changeMsisdnDialog" max-width="500">
      <v-card>
        <v-card-title>Change MSISDN</v-card-title>
        <v-card-text>
          <div v-if="changeMsisdnItem" class="mb-4">
            <strong>SIM ID:</strong> {{ changeMsisdnItem.id }}<br>
            <strong>Serial:</strong> {{ changeMsisdnItem.serial || '-' }}<br>
            <strong>Current MSISDN:</strong> {{ changeMsisdnItem.msisdn || '-' }}
          </div>
          <v-autocomplete
              v-model="newMsisdnId"
              :items="availableMsisdns"
              item-title="msisdn"
              item-value="id"
              label="Select New MSISDN"
              hint="Choose a new MSISDN for this SIM"
              persistent-hint
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="changeMsisdnDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="executeChangeMsisdn" :loading="changeMsisdnLoading" :disabled="!newMsisdnId">
            Change MSISDN
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit SIM Dialog -->
    <v-dialog v-model="editDialog" max-width="500">
      <v-card>
        <v-card-title>Edit SIM</v-card-title>
        <v-card-text>
          <div v-if="editItem" class="mb-4">
            <strong>SIM ID:</strong> {{ editItem.id }}<br>
            <strong>Serial:</strong> {{ editItem.serial || '-' }}<br>
            <strong>MSISDN:</strong> {{ editItem.msisdn || '-' }}
          </div>
          <v-text-field
              v-model="editFormData.seria_echipament"
              label="Seria Echipament"
              density="compact"
              class="mb-2"
          ></v-text-field>
          <v-text-field
              v-model="editFormData.nui"
              label="NUI"
              density="compact"
              class="mb-2"
          ></v-text-field>
          <v-text-field
              v-model="editFormData.technician"
              label="Technician"
              density="compact"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="editDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="executeEdit" :loading="editLoading">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Terminate Dialog with Options -->
    <v-dialog v-model="terminateDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert</v-icon>
          Terminate SIM
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="mb-3">Choose termination type for this SIM:</p>

          <v-alert type="info" density="compact" class="mb-3">
            <div><strong>SIM ID:</strong> {{ terminateItem?.id }}</div>
            <div><strong>Serial:</strong> {{ terminateItem?.serial || '-' }}</div>
            <div><strong>MSISDN:</strong> {{ terminateItem?.msisdn_number || '-' }}</div>
            <div><strong>IMSI:</strong> {{ terminateItem?.imsi_number || '-' }}</div>
          </v-alert>

          <v-alert type="warning" density="compact" class="mb-3">
            <strong>Terminate only CS:</strong> SIM remains provisioned in mobile network. Can be renewed later.
          </v-alert>

          <v-alert v-if="hasTerminateSubscriberRole" type="error" density="compact">
            <strong>Terminate CS + Deprovision:</strong> SIM will be deprovisioned from mobile network. MSISDN/IMSI will be released.
          </v-alert>
        </v-card-text>
        <v-card-actions class="flex-column pa-4">
          <v-btn
            color="warning"
            block
            class="mb-2"
            @click="executeTerminate(false)"
            :loading="terminateLoading"
          >
            <v-icon class="mr-2">mdi-stop-circle</v-icon>
            Terminate only CS (keep provisioned)
          </v-btn>

          <v-btn
            v-if="hasTerminateSubscriberRole"
            color="error"
            block
            class="mb-2"
            @click="executeTerminate(true)"
            :loading="terminateLoading"
          >
            <v-icon class="mr-2">mdi-delete-forever</v-icon>
            Terminate CS + Deprovision
          </v-btn>

          <v-btn
            variant="text"
            block
            @click="terminateDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Renew Client Service Dialog -->
    <v-dialog v-model="renewDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-success text-white">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          Renew Client Service
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="info" class="mb-3">
            A new Client Service will be created with the same data as the previous one.
          </v-alert>

          <v-table density="compact" v-if="renewItem">
            <tbody>
              <tr><td class="font-weight-bold">SIM ID:</td><td>{{ renewItem.id }}</td></tr>
              <tr><td class="font-weight-bold">Serial:</td><td>{{ renewItem.serial || '-' }}</td></tr>
              <tr><td class="font-weight-bold">IMSI:</td><td>{{ renewItem.imsi_number || '-' }}</td></tr>
              <tr><td class="font-weight-bold">MSISDN:</td><td>{{ renewItem.msisdn_number || '-' }}</td></tr>
              <tr><td class="font-weight-bold">Client:</td><td>{{ renewItem.client_name || '-' }}</td></tr>
              <tr><td class="font-weight-bold">Previous Service:</td><td>{{ renewItem.service_name || renewItem.service_short_name || '-' }}</td></tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="renewDialog = false">Cancel</v-btn>
          <v-btn color="success" @click="executeRenew" :loading="renewLoading">
            <v-icon class="mr-2">mdi-check</v-icon>
            Renew
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reprovision Dialog (for terminated SIMs) -->
    <v-dialog v-model="reprovisionDialog" max-width="600">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-sim</v-icon>
          Reprovision SIM
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="info" density="compact" class="mb-4">
            <div><strong>SIM ID:</strong> {{ reprovisionItem?.id }}</div>
            <div><strong>Serial:</strong> {{ reprovisionItem?.serial || '-' }}</div>
            <div><strong>MSISDN:</strong> {{ reprovisionItem?.msisdn || '-' }}</div>
            <div><strong>Client:</strong> {{ reprovisionItem?.client_name || '-' }}</div>
          </v-alert>

          <v-alert type="warning" variant="tonal" class="mb-4">
            <strong>Nota:</strong> SIM-ul anterior a fost terminat. Se va crea un SIM nou si se va proviziona cu MSISDN-ul existent.
          </v-alert>

          <!-- IMSI Selection -->
          <div class="text-subtitle-1 font-weight-bold mb-2">1. Selecteaza IMSI</div>

          <v-alert v-if="reprovisionNoImsisAvailable" type="error" variant="tonal" class="mb-4">
            <strong>Nu exista IMSI-uri disponibile pentru acest client.</strong><br>
            Alocati IMSI-uri clientului inainte de provizionare.
          </v-alert>

          <template v-else>
            <v-row class="mb-2">
              <v-col cols="12" md="8">
                <v-text-field
                    v-model="reprovisionImsiSearchTerm"
                    label="Cauta IMSI/Serial"
                    placeholder="Introdu IMSI sau serial"
                    density="compact"
                    clearable
                    @keyup.enter="searchReprovisionImsis"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn color="primary" size="small" @click="searchReprovisionImsis" :loading="reprovisionImsiSearchLoading">
                  Cauta
                </v-btn>
              </v-col>
            </v-row>

            <v-autocomplete
                v-model="selectedReprovisionImsi"
                :items="reprovisionAvailableImsis"
                item-title="display_name"
                item-value="id"
                label="Selecteaza IMSI"
                hint="Alege un IMSI disponibil"
                persistent-hint
                :loading="reprovisionImsiSearchLoading"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:subtitle>
                    Serial: {{ item.raw.serial }}
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
          </template>

          <!-- Product Code Selection -->
          <div class="text-subtitle-1 font-weight-bold mb-2 mt-4">2. Selecteaza Product Code</div>
          <v-select
              v-model="selectedReprovisionProduct"
              :items="reprovisionProductCodesWithDescription"
              item-title="display"
              item-value="name"
              label="Selecteaza Product Code"
              hint="Codul produsului pentru provizionare la MNO"
              persistent-hint
              :loading="reprovisionProductsLoading"
              :rules="[v => !!v || 'Product code is required']"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="reprovisionDialog = false">Anuleaza</v-btn>
          <v-btn
              color="primary"
              @click="executeReprovision"
              :loading="reprovisionLoading"
              :disabled="!selectedReprovisionImsi || !selectedReprovisionProduct || reprovisionNoImsisAvailable"
          >
            Provizioneaza
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <!-- Add to Inventory Dialog -->
    <v-dialog v-model="inventoryDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-teal text-white">
          <v-icon class="mr-2">mdi-package-variant</v-icon>
          Add SIM to Inventory
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="info" density="compact" class="mb-4">
            <div><strong>SIM ID:</strong> {{ inventoryItem?.id }}</div>
            <div><strong>Serial:</strong> {{ inventoryItem?.serial || '-' }}</div>
            <div><strong>IMSI:</strong> {{ inventoryItem?.imsi_number || '-' }}</div>
          </v-alert>

          <v-select
              v-model="selectedInventoryStatusId"
              :items="inventoryStatuses"
              item-title="name"
              item-value="id"
              label="Inventory Status"
              variant="outlined"
              density="compact"
              :rules="[v => !!v || 'Status is required']"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="inventoryDialog = false">Cancel</v-btn>
          <v-btn
              color="teal"
              variant="flat"
              @click="addToInventory"
              :loading="inventoryLoading"
              :disabled="!selectedInventoryStatusId"
          >
            Add to Inventory
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Client Service Dialog -->
    <AddNewClientService
      :show="addClientServiceDialog"
      :renew-data="addClientServiceData"
      @close="addClientServiceDialog = false"
      @serviceAdded="onClientServiceAdded"
    />

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" color="red" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Error Alert Dialog -->
    <v-dialog v-model="errorDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-error text-white d-flex align-center">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Error
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="text-h6 text-center">{{ errorDialogMessage }}</div>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="primary" variant="flat" size="large" @click="errorDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Subscriber Status Dialog -->
    <SubscriberStatusDialog
      :show="showSubscriberStatusDialog"
      :data="subscriberStatusData"
      :loading="loadingSubscriberStatus"
      @close="showSubscriberStatusDialog = false"
    />
  </v-container>
</template>

<script>
import {getSimsList, getSimStatuses, provisionSim, getAvailableImsisForClient, refreshSim, terminateSim, suspendSim, reactivateSim, changeSimMsisdn, updateSim, renewClientService, reprovisionTerminatedSim} from '@/composables/useGetSimsAPI'
import {updateImsiInfo} from '@/composables/useGetAllocatedImsisAPI'
import {getAvailableMsisdnsList, getMsisdnClasses} from '@/composables/useGetAvailableMsisdnsAPI'
import {addSimsToInventory, getInventoryStatuses} from '@/composables/useGoodsInventoryAPI'
import {getProductCodesForProvisioning} from '@/composables/useProductsAPI'
import {getServicesForAddClientService} from '@/composables/useGetServicesForAddClientService'
import {log} from '@/composables/useLog'
import {getDisplaySettings} from '@/composables/useDisplaySettingsAPI'
import NavBar from "@/components/NavBar.vue";
import SimSearchFilters from "@/components/SimSearchFilters.vue";
import AddNewClientService from "@/components/AddNewClientService.vue";
import SubscriberStatusDialog from "@/components/dialogs/SubscriberStatusDialog.vue";

export default {
  name: 'SimsList',
  components: {NavBar, SimSearchFilters, AddNewClientService, SubscriberStatusDialog},
  data() {
    return {
      // Data
      sims: [],
      simStatuses: [],
      totalCount: 0,
      loading: false,
      isDistributor: false,
      expanded: [],
      initialLoadDone: false,

      // Filters
      searchTerm: '',
      selectedStatusId: 'all_except_terminated',
      includeSubclients: false,
      selectedClientId: null,
      hasSubclients: false,
      subclients: [],
      filterSimId: null,

      // Pagination
      page: 1,
      perPage: 20,
      sortBy: [{key: 'id', order: 'desc'}],

      // Table headers
      headers: [
        {title: 'ID', key: 'id', sortable: true},
        {title: 'Serial', key: 'serial', sortable: true},
        {title: 'MSISDN', key: 'msisdn', sortable: true},
        {title: 'Status retea', key: 'sim_status_name', sortable: true},
        {title: 'Client', key: 'client_name', sortable: true},
        {title: 'Servicii', key: 'services_count', sortable: false},
        {title: 'Expires', key: 'expiration_date', sortable: true},
        {title: 'Seria Echipament', key: 'seria_echipament', sortable: false},
        {title: 'NUI', key: 'nui', sortable: false},
        {title: 'Technician', key: 'technician', sortable: false},
        {title: 'Actions', key: 'actions', sortable: false, width: '280px'}
      ],

      // Action dialog
      actionDialog: false,
      actionLoading: false,
      actionItem: null,
      actionType: null,

      // Provision dialog
      provisionDialog: false,
      provisionLoading: false,
      provisionItem: null,
      selectedMsisdnForProvision: null,
      availableMsisdns: [],
      hasSelectMsisdnRole: false,
      msisdnClasses: [],
      selectedMsisdnClassId: 99,
      msisdnLike: '',
      msisdnSearchLoading: false,
      // Product selection for provision
      availableProducts: [],
      selectedProductForProvision: null,
      productsLoading: false,
      // IMSI selection for provision
      needsImsiSelection: false,
      needsMsisdnSelection: false,
      availableImsis: [],
      selectedImsiForProvision: null,
      imsiSearchTerm: '',
      imsiSearchLoading: false,
      noImsisAvailable: false,

      // Change MSISDN dialog
      changeMsisdnDialog: false,
      changeMsisdnLoading: false,
      changeMsisdnItem: null,
      newMsisdnId: null,

      // Edit dialog
      editDialog: false,
      editLoading: false,
      editItem: null,
      editFormData: {
        seria_echipament: '',
        nui: '',
        technician: ''
      },

      // Terminate dialog
      terminateDialog: false,
      terminateLoading: false,
      terminateItem: null,
      hasTerminateSubscriberRole: false,

      // Renew dialog
      renewDialog: false,
      renewLoading: false,
      renewItem: null,

      // Reprovision dialog (for terminated SIMs)
      reprovisionDialog: false,
      reprovisionLoading: false,
      reprovisionItem: null,
      selectedReprovisionImsi: null,
      reprovisionImsiSearchTerm: '',
      reprovisionAvailableImsis: [],
      reprovisionImsiSearchLoading: false,
      reprovisionNoImsisAvailable: false,
      reprovisionAvailableProducts: [],
      selectedReprovisionProduct: null,
      reprovisionProductsLoading: false,

      // Snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      cacheRebuilding: false,

      // Error Dialog
      errorDialog: false,
      errorDialogMessage: '',

      // Inventory dialog
      inventoryDialog: false,
      inventoryLoading: false,
      inventoryItem: null,
      inventoryStatuses: [],
      selectedInventoryStatusId: null,

      // Update IMSI Info
      updatingImsiInfo: false,
      updateImsiInfoItemId: null,
      updateImsiInfoMessage: '',
      updateImsiInfoSuccess: false,

      // Subscriber Status Dialog
      showSubscriberStatusDialog: false,
      loadingSubscriberStatus: false,
      subscriberStatusData: null,

      // Add Client Service dialog
      addClientServiceDialog: false,
      addClientServiceData: null,
      addClientServiceLoading: false
    }
  },

  computed: {
    token() {
      return this.$store.getters.getToken
    },

    totalPages() {
      return Math.ceil(this.totalCount / this.perPage)
    },

    hasSearchCriteria() {
      if (!this.isDistributor) return true
      const search = this.searchTerm ? this.searchTerm.trim().replace(/\*/g, '') : ''
      return (search.length >= 3) ||
             (this.selectedClientId !== null) ||
             (this.filterSimId !== null) ||
             (this.selectedStatusId && !['all_except_terminated', 'all'].includes(String(this.selectedStatusId)))
    },

    productCodesWithDescription() {
      return this.availableProducts.map(pc => ({
        ...pc,
        display: pc.description ? `${pc.name} - ${pc.description}` : pc.name
      }))
    },

    reprovisionProductCodesWithDescription() {
      return this.reprovisionAvailableProducts.map(pc => ({
        ...pc,
        display: pc.description ? `${pc.name} - ${pc.description}` : pc.name
      }))
    },

    actionDialogTitle() {
      const titles = {
        'terminate': 'Confirm Termination',
        'suspend': 'Confirm Suspension',
        'reactivate': 'Confirm Reactivation',
        'refresh': 'Confirm Refresh'
      }
      return titles[this.actionType] || 'Confirm Action'
    },

    actionDialogMessage() {
      const messages = {
        'terminate': 'Are you sure you want to terminate this SIM?',
        'suspend': 'Are you sure you want to suspend this SIM?',
        'reactivate': 'Are you sure you want to reactivate this SIM?',
        'refresh': 'Are you sure you want to refresh this SIM status?'
      }
      return messages[this.actionType] || 'Are you sure?'
    },

    actionDialogButtonText() {
      const texts = {
        'terminate': 'Terminate',
        'suspend': 'Suspend',
        'reactivate': 'Reactivate',
        'refresh': 'Refresh'
      }
      return texts[this.actionType] || 'Confirm'
    },

    actionDialogButtonColor() {
      const colors = {
        'terminate': 'error',
        'suspend': 'orange',
        'reactivate': 'success',
        'refresh': 'info'
      }
      return colors[this.actionType] || 'primary'
    }
  },

  async mounted() {
    // Restore filters from query params
    const query = this.$route.query
    if (query.search) {
      this.searchTerm = query.search
      // When searching from URL, show all statuses to find terminated SIMs too
      if (!query.status) {
        this.selectedStatusId = 'all'
      }
    }
    if (query.status) {
      // Status can be 'all', 'all_except_terminated', or a numeric ID
      const statusValue = query.status
      if (statusValue === 'all' || statusValue === 'all_except_terminated') {
        this.selectedStatusId = statusValue
      } else {
        // Convert to number for numeric status IDs
        this.selectedStatusId = parseInt(statusValue, 10)
      }
    }
    if (query.client_id) {
      this.selectedClientId = parseInt(query.client_id, 10)
    }
    if (query.include_subclients === 'false') {
      this.includeSubclients = false
    }
    if (query.page) {
      this.page = parseInt(query.page, 10)
    }
    if (query.sim_id) {
      this.filterSimId = parseInt(query.sim_id, 10)
      // When filtering by sim_id, show all statuses
      this.selectedStatusId = 'all'
    }

    // Set isDistributor from user data
    const user = this.$store.getters.getUser
    this.isDistributor = user?.userClient?.is_distributor === true

    // Load display settings (from store cache or fetch)
    let ds = this.$store.state.displaySettings
    if (!ds || !ds.default_page_size_sims) {
      const dsResult = await getDisplaySettings(this.token)
      if (!dsResult.error && dsResult.display_settings) {
        ds = dsResult.display_settings
        this.$store.commit('setDisplaySettings', ds)
      }
    }
    if (ds && ds.default_page_size_sims) {
      this.perPage = ds.default_page_size_sims
    }

    await this.loadSimStatuses()

    // Only auto-load if there are search criteria from URL params
    if (this.hasSearchCriteria) {
      await this.fetchSims()
    }
  },

  methods: {
    async loadSimStatuses() {
      const result = await getSimStatuses(this.token)
      if (!result.error) {
        this.simStatuses = result.sim_statuses
      }
    },

    async fetchSims() {
      if (!this.hasSearchCriteria) {
        this.sims = []
        this.totalCount = 0
        this.initialLoadDone = false
        return
      }
      this.loading = true
      this.initialLoadDone = true
      try {
        const options = {
          page: this.page,
          per_page: this.perPage,
          include_subclients: this.includeSubclients
        }

        const trimmedSearch = this.searchTerm ? this.searchTerm.trim() : ''
        if (trimmedSearch && trimmedSearch.replace(/\*/g, '').length >= 3) {
          options.search_term = trimmedSearch
        }

        if (this.selectedStatusId) {
          options.sim_status_id = this.selectedStatusId
        }

        if (this.selectedClientId) {
          options.client_id = this.selectedClientId
        }

        if (this.filterSimId) {
          options.sim_id = this.filterSimId
        }

        if (this.sortBy && this.sortBy.length > 0) {
          options.sort_by = this.sortBy[0].key
          options.sort_order = this.sortBy[0].order
        }

        const result = await getSimsList(this.token, options)

        if (result.error) {
          this.showSnackbar(result.message, 'error')
        } else {
          this.sims = result.sims
          this.totalCount = result.total_count
          this.hasSubclients = result.filters?.has_subclients || false
          this.subclients = result.filters?.subclients || []
          this.hasSelectMsisdnRole = result.user_permissions?.has_select_msisdn_role || false
          this.hasTerminateSubscriberRole = result.user_permissions?.has_terminate_subscriber_role || false
        }
      } finally {
        this.loading = false
      }
    },

    search() {
      this.page = 1
      this.fetchSims()
    },

    resetFilters() {
      this.searchTerm = ''
      this.selectedStatusId = 'all_except_terminated'
      this.includeSubclients = false
      this.selectedClientId = null
      this.filterSimId = null
      this.page = 1
      this.sortBy = [{key: 'id', order: 'desc'}]
      this.sims = []
      this.totalCount = 0
      this.initialLoadDone = false
    },

    onHierarchyLoaded({ hasSubclients }) {
      this.hasSubclients = hasSubclients
    },

    onPageChange(newPage) {
      this.page = newPage
      this.fetchSims()
    },

    onSortChange(newSort) {
      this.sortBy = newSort
      this.fetchSims()
    },

    formatStatusLabel(name) {
      if (!name) return '-'
      return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    getStatusColor(status) {
      const statusLower = status?.toLowerCase()
      switch (statusLower) {
        case 'active':
          return 'success'
        case 'not_provisioned':
          return 'info'
        case 'pending':
          return 'warning'
        case 'suspended':
          return 'orange'
        case 'terminated':
          return 'grey'
        case 'expired':
          return 'error'
        default:
          return 'grey'
      }
    },

    getCsStatusColor(status) {
      const statusLower = status?.toLowerCase()
      switch (statusLower) {
        case 'active':
          return 'success'
        case 'expired':
          return 'error'
        case 'suspended':
          return 'orange'
        case 'terminated':
          return 'grey'
        case 'waiting_for_payment':
          return 'warning'
        default:
          return 'grey'
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
    },

    isExpiringSoon(dateStr) {
      if (!dateStr) return false
      const expirationDate = new Date(dateStr)
      const today = new Date()
      const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24))
      return daysUntilExpiration <= 30 && daysUntilExpiration >= 0
    },

    getRowProps(item) {
      const statusName = item.item?.sim_status_name?.toLowerCase()
      if (statusName === 'terminated') {
        return {style: 'background-color: #EEEEEE;'}
      }
      if (statusName === 'suspended') {
        return {style: 'background-color: #FFF3E0;'}
      }
      if (statusName === 'not_provisioned') {
        return {style: 'background-color: #E3F2FD;'}
      }
      return {}
    },

    // Navigation methods
    navigateToClient(clientId) {
      if (clientId) {
        this.$router.push({name: 'editClient', query: {id: clientId}})
      }
    },

    navigateToClientService(clientServiceId) {
      if (clientServiceId) {
        this.$router.push({path: '/clientServices', query: {cs_id: clientServiceId}})
      }
    },

    navigateToSimServices(simId) {
      if (simId) {
        this.$router.push({path: '/clientServices', query: {sim_id: simId, status: 'active'}})
      }
    },

    navigateToSimDetails(simId) {
      if (simId) {
        // Save current filters to query params before navigating
        const query = this.buildFilterQuery()
        // Update current URL with filters first (so back button returns here with filters)
        this.$router.replace({name: 'sims', query}).then(() => {
          // Then navigate to the SIM details page
          this.$router.push({name: 'sim', params: {id: simId}})
        })
      }
    },

    navigateToTraffic(item) {
      const clientId = item.client_id || item.clientId
      this.$router.push({
        name: 'trafficReports',
        query: { sim_id: item.id, client_id: clientId }
      })
    },

    navigateToNetworkServices(item) {
      this.$router.push({ name: 'sim-network-services', params: { id: item.id } })
    },

    // Build query params from current filter state
    buildFilterQuery() {
      const query = {}
      if (this.searchTerm) {
        query.search = this.searchTerm
      }
      if (this.selectedStatusId && this.selectedStatusId !== 'all_except_terminated') {
        query.status = this.selectedStatusId
      }
      if (this.selectedClientId) {
        query.client_id = this.selectedClientId
      }
      if (!this.includeSubclients) {
        query.include_subclients = 'false'
      }
      if (this.page > 1) {
        query.page = this.page
      }
      return query
    },

    navigateToOrder(orderId) {
      if (orderId) {
        this.$router.push({path: '/orders', query: {search: orderId}})
      }
    },

    navigateToImsi(imsiId) {
      if (imsiId) {
        this.$router.push({path: '/clientServices', query: {search: imsiId}})
      }
    },

    navigateToMsisdn(msisdnId) {
      if (msisdnId) {
        this.$router.push({path: '/clientServices', query: {search: msisdnId}})
      }
    },

    navigateToMsisdnList(msisdnId) {
      if (msisdnId) {
        this.$router.push({path: '/msisdns', query: {msisdn_id: msisdnId}})
      }
    },

    navigateToImsiList(serial) {
      if (serial) {
        this.$router.push({name: 'imsis', query: {search: serial}})
      }
    },

    // Action methods
    confirmTerminate(item) {
      this.terminateItem = item
      this.terminateDialog = true
    },

    async executeTerminate(deprovision) {
      if (!this.terminateItem) return

      this.terminateLoading = true
      try {
        const result = await terminateSim(this.token, this.terminateItem.id, deprovision)

        if (result.error) {
          this.showSnackbar(result.message, 'error')
        } else {
          this.showSnackbar(result.message || 'Termination completed successfully', 'success')
          this.terminateDialog = false
          this.terminateItem = null
          await this.fetchSims()
        }
      } finally {
        this.terminateLoading = false
      }
    },

    openRenewDialog(item) {
      this.renewItem = item
      this.renewDialog = true
    },

    async executeRenew() {
      if (!this.renewItem) return

      this.renewLoading = true
      try {
        const result = await renewClientService(this.token, this.renewItem.id)

        if (result.error) {
          this.showSnackbar(result.message, 'error')
        } else {
          this.showSnackbar(result.message || 'Client Service renewed successfully', 'success')
          this.renewDialog = false
          this.renewItem = null
          await this.fetchSims()
        }
      } finally {
        this.renewLoading = false
      }
    },

    // Reprovision methods (for terminated SIMs)
    async openReprovisionDialog(item) {
      this.reprovisionItem = item
      this.selectedReprovisionImsi = null
      this.reprovisionImsiSearchTerm = ''
      this.reprovisionAvailableImsis = []
      this.reprovisionNoImsisAvailable = false
      this.selectedReprovisionProduct = null
      this.reprovisionAvailableProducts = []

      this.reprovisionDialog = true

      // Load available products
      await this.loadReprovisionProducts()

      // Load available IMSIs
      await this.searchReprovisionImsis()
    },

    async loadReprovisionProducts() {
      this.reprovisionProductsLoading = true
      try {
        const result = await getProductCodesForProvisioning(this.token)
        if (!result.error) {
          this.reprovisionAvailableProducts = result.product_codes || []
        } else {
          this.showSnackbar('Error loading product codes', 'error')
        }
      } catch (err) {
        log('Error loading reprovision product codes:', err)
        this.showSnackbar('Error loading product codes', 'error')
      } finally {
        this.reprovisionProductsLoading = false
      }
    },

    async searchReprovisionImsis() {
      this.reprovisionImsiSearchLoading = true
      this.reprovisionNoImsisAvailable = false
      try {
        const clientId = this.reprovisionItem?.client_id
        if (!clientId) {
          this.showSnackbar('No client ID found for this SIM', 'error')
          return
        }

        const searchTerm = this.reprovisionImsiSearchTerm ? this.reprovisionImsiSearchTerm.trim() : ''
        const result = await getAvailableImsisForClient(this.token, clientId, searchTerm)
        if (!result.error) {
          this.reprovisionAvailableImsis = result.imsis || []
          this.reprovisionNoImsisAvailable = this.reprovisionAvailableImsis.length === 0
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } catch (err) {
        this.showSnackbar('Error searching IMSIs', 'error')
      } finally {
        this.reprovisionImsiSearchLoading = false
      }
    },

    async executeReprovision() {
      if (!this.reprovisionItem) return

      // Validate Product Code selection
      if (!this.selectedReprovisionProduct) {
        this.showSnackbar('Selectati un Product Code', 'warning')
        return
      }

      // Validate IMSI selection
      if (!this.selectedReprovisionImsi) {
        this.showSnackbar('Selectati un IMSI', 'warning')
        return
      }

      this.reprovisionLoading = true
      try {
        const result = await reprovisionTerminatedSim(
          this.token,
          this.selectedReprovisionImsi,
          this.reprovisionItem.msisdn_id,
          this.selectedReprovisionProduct
        )

        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la reprovizionare', 'error')
        } else {
          this.showSnackbar(result.message || 'SIM reprovizionat cu succes', 'success')
          this.reprovisionDialog = false
          this.reprovisionItem = null
          // Refresh after a short delay to show the pending state
          setTimeout(() => this.fetchSims(), 1000)
        }
      } finally {
        this.reprovisionLoading = false
      }
    },

    confirmSuspend(item) {
      this.actionItem = item
      this.actionType = 'suspend'
      this.actionDialog = true
    },

    confirmReactivate(item) {
      this.actionItem = item
      this.actionType = 'reactivate'
      this.actionDialog = true
    },

    confirmRefresh(item) {
      this.actionItem = item
      this.actionType = 'refresh'
      this.actionDialog = true
    },

    async executeAction() {
      if (!this.actionItem || !this.actionType) return

      this.actionLoading = true
      try {
        let result
        switch (this.actionType) {
          case 'suspend':
            result = await suspendSim(this.token, this.actionItem.id)
            break
          case 'reactivate':
            result = await reactivateSim(this.token, this.actionItem.id)
            break
          case 'refresh':
            result = await refreshSim(this.token, this.actionItem.id)
            break
        }

        if (result.error) {
          this.showSnackbar(result.message, 'error')
        } else {
          this.showSnackbar(result.message || 'Action completed successfully', 'success')
          this.actionDialog = false
          this.actionItem = null
          await this.fetchSims()
        }
      } finally {
        this.actionLoading = false
      }
    },

    async openProvisionDialog(item) {
      this.provisionItem = item
      this.selectedMsisdnForProvision = null
      this.selectedImsiForProvision = null
      this.selectedProductForProvision = null
      this.msisdnLike = ''
      this.imsiSearchTerm = ''
      this.availableMsisdns = []
      this.availableImsis = []
      this.availableProducts = []
      this.noImsisAvailable = false

      // Determine what selections are needed
      // If SIM already has msisdn_id, skip MSISDN selection
      this.needsMsisdnSelection = !item.msisdn_id && this.hasSelectMsisdnRole
      // If SIM doesn't have imsi_id, need to select IMSI
      this.needsImsiSelection = !item.imsi_id

      this.provisionDialog = true

      // Load available products
      await this.loadProducts()

      // Load MSISDN classes and list if needed
      if (this.needsMsisdnSelection) {
        try {
          const classesResult = await getMsisdnClasses(this.token)
          if (!classesResult.error) {
            this.msisdnClasses = classesResult.msisdns_classes || []
          }
        } catch (err) {
          this.showSnackbar('Error loading MSISDN classes', 'error')
        }
        await this.searchMsisdns()
      }

      // Load available IMSIs if needed
      if (this.needsImsiSelection) {
        await this.searchImsis()
      }
    },

    async loadProducts() {
      this.productsLoading = true
      try {
        const result = await getProductCodesForProvisioning(this.token)
        if (!result.error) {
          this.availableProducts = result.product_codes || []
        } else {
          this.showSnackbar('Error loading product codes', 'error')
        }
      } catch (err) {
        log('Error loading product codes:', err)
        this.showSnackbar('Error loading product codes', 'error')
      } finally {
        this.productsLoading = false
      }
    },

    async searchImsis() {
      this.imsiSearchLoading = true
      this.noImsisAvailable = false
      try {
        const clientId = this.provisionItem?.client_id
        if (!clientId) {
          this.showSnackbar('No client ID found for this SIM', 'error')
          return
        }

        const searchTerm = this.imsiSearchTerm ? this.imsiSearchTerm.trim() : ''
        const result = await getAvailableImsisForClient(this.token, clientId, searchTerm)
        if (!result.error) {
          this.availableImsis = result.imsis || []
          this.noImsisAvailable = this.availableImsis.length === 0
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } catch (err) {
        this.showSnackbar('Error searching IMSIs', 'error')
      } finally {
        this.imsiSearchLoading = false
      }
    },

    async searchMsisdns() {
      this.msisdnSearchLoading = true
      try {
        const options = {
          msisdn_class_id: this.selectedMsisdnClassId,
          limit: 100
        }
        if (this.msisdnLike && this.msisdnLike.trim()) {
          options.msisdn_like = this.msisdnLike.trim()
        }
        // Pass imsi_id to filter MSISDNs by same network and mno_status_id=1
        const imsiId = this.provisionItem?.imsi_id || this.selectedImsiForProvision
        if (imsiId) {
          options.imsi_id = imsiId
        }

        const result = await getAvailableMsisdnsList(this.token, options)
        if (!result.error) {
          this.availableMsisdns = result.msisdns_list || []
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } catch (err) {
        this.showSnackbar('Error searching MSISDNs', 'error')
      } finally {
        this.msisdnSearchLoading = false
      }
    },

    async executeProvision() {
      if (!this.provisionItem) return

      // Validate Product Code selection (always required)
      if (!this.selectedProductForProvision) {
        this.showSnackbar('Please select a Product Code', 'warning')
        return
      }

      // Validate MSISDN selection if needed
      if (this.needsMsisdnSelection && !this.selectedMsisdnForProvision) {
        this.showSnackbar('Please select an MSISDN', 'warning')
        return
      }

      // Validate IMSI selection if needed
      if (this.needsImsiSelection && !this.selectedImsiForProvision) {
        this.showSnackbar('Please select an IMSI', 'warning')
        return
      }

      this.provisionLoading = true
      try {
        // Pass msisdn_id only if user selected one, otherwise backend will auto-assign
        const msisdnId = this.needsMsisdnSelection ? this.selectedMsisdnForProvision : null
        // Pass imsi_id only if user selected one
        const imsiId = this.needsImsiSelection ? this.selectedImsiForProvision : null
        // Pass mno_product (the product name)
        const mnoProduct = this.selectedProductForProvision

        const result = await provisionSim(this.token, this.provisionItem.id, msisdnId, imsiId, mnoProduct, this.provisionItem.client_service_id)

        if (result.error) {
          // Show error in a bigger dialog instead of snackbar
          this.errorDialogMessage = result.message
          this.errorDialog = true
          this.provisionDialog = false
        } else {
          this.showSnackbar(result.message || 'SIM provisioning started', 'success')
          this.provisionDialog = false
          this.provisionItem = null
          // Refresh after a short delay to show the pending state
          setTimeout(() => this.fetchSims(), 1000)
        }
      } finally {
        this.provisionLoading = false
      }
    },

    async openChangeMsisdnDialog(item) {
      this.changeMsisdnItem = item
      this.newMsisdnId = null
      this.changeMsisdnDialog = true

      // Load available MSISDNs
      try {
        const result = await getAvailableMsisdnsList(this.token, {})
        if (!result.error) {
          this.availableMsisdns = result.msisdns_list || []
        }
      } catch (err) {
        this.showSnackbar('Error loading available MSISDNs', 'error')
      }
    },

    async executeChangeMsisdn() {
      if (!this.changeMsisdnItem || !this.newMsisdnId) return

      this.changeMsisdnLoading = true
      try {
        const result = await changeSimMsisdn(this.token, this.changeMsisdnItem.id, this.newMsisdnId)

        if (result.error) {
          this.showSnackbar(result.message, 'error')
        } else {
          this.showSnackbar(result.message || 'MSISDN changed successfully', 'success')
          this.changeMsisdnDialog = false
          this.changeMsisdnItem = null
          await this.fetchSims()
        }
      } finally {
        this.changeMsisdnLoading = false
      }
    },

    openEditDialog(item) {
      this.editItem = item
      this.editFormData.seria_echipament = item.seria_echipament || ''
      this.editFormData.nui = item.nui || ''
      this.editFormData.technician = item.technician || ''
      this.editDialog = true
    },

    async executeEdit() {
      if (!this.editItem) return

      this.editLoading = true
      try {
        const result = await updateSim(this.token, this.editItem.id, {
          seria_echipament: this.editFormData.seria_echipament,
          nui: this.editFormData.nui,
          technician: this.editFormData.technician
        })

        if (result.error) {
          this.showSnackbar(result.message, 'error')
        } else {
          this.showSnackbar(result.message || 'SIM updated successfully', 'success')
          this.editDialog = false
          this.editItem = null
          await this.fetchSims()
        }
      } finally {
        this.editLoading = false
      }
    },

    // Inventory methods
    async openInventoryDialog(item) {
      this.inventoryItem = item
      this.selectedInventoryStatusId = null

      // Load inventory statuses if not already loaded
      if (this.inventoryStatuses.length === 0) {
        const result = await getInventoryStatuses(this.token)
        if (!result.error) {
          this.inventoryStatuses = result.inventory_statuses || []
        }
      }

      this.inventoryDialog = true
    },

    async addToInventory() {
      if (!this.inventoryItem || !this.selectedInventoryStatusId) return

      this.inventoryLoading = true
      try {
        const result = await addSimsToInventory(
          this.token,
          [this.inventoryItem.id],
          this.selectedInventoryStatusId
        )

        if (!result.error) {
          this.showSnackbar(result.message || 'SIM added to inventory', 'success')
          this.inventoryDialog = false
        } else {
          this.showSnackbar(result.message || 'Error adding to inventory', 'error')
        }
      } catch (err) {
        log('Error adding to inventory:', err)
        this.showSnackbar('Error adding to inventory', 'error')
      } finally {
        this.inventoryLoading = false
      }
    },

    async updateSimSubscriberInfo(item) {
      if (!item || !item.imsi_id) {
        this.showSnackbar('No IMSI ID available for this SIM', 'error')
        return
      }

      // Open dialog and show loading
      this.showSubscriberStatusDialog = true
      this.loadingSubscriberStatus = true
      this.subscriberStatusData = null
      this.updatingImsiInfo = true
      this.updateImsiInfoItemId = item.id
      this.updateImsiInfoMessage = ''

      try {
        const result = await updateImsiInfo(this.token, item.imsi_id)

        // Store the full result for the dialog
        this.subscriberStatusData = result

        if (!result.error) {
          this.updateImsiInfoMessage = result.message || 'Info actualizat cu succes'
          this.updateImsiInfoSuccess = true
          // Refresh the list to show updated status
          await this.fetchSims()
        } else {
          this.updateImsiInfoMessage = result.message || 'Eroare la actualizare info'
          this.updateImsiInfoSuccess = false
        }
      } catch (err) {
        log('Error updating IMSI info:', err)
        this.updateImsiInfoMessage = 'Eroare la actualizare info'
        this.updateImsiInfoSuccess = false
        this.subscriberStatusData = { error: true, message: 'Eroare la actualizare info' }
      } finally {
        this.updatingImsiInfo = false
        this.loadingSubscriberStatus = false
      }
    },

    async openAddClientServiceDialog(item) {
      if (!item) return

      this.addClientServiceLoading = true
      try {
        // Fetch available services for the client
        const response = await getServicesForAddClientService(this.token, {
          target_client_id: item.client_id,
          include_subclients: false
        })

        if (response.error) {
          this.showSnackbar(response.message || 'Eroare la incarcarea serviciilor', 'error')
          return
        }

        // Filter services to only show imsi_subscription type
        const allServices = response.services || []
        const imsiSubscriptionServices = allServices.filter(s => s.service_type_name === 'imsi_subscription')

        if (imsiSubscriptionServices.length === 0) {
          this.showSnackbar('Nu exista servicii de tip imsi_subscription disponibile pentru acest client', 'warning')
          return
        }

        // Get existing services on this SIM (from client services linked to this IMSI)
        // For now, we'll pass empty array - the component will handle this
        const existingServices = []

        // Prepare renewData for SIM service addition
        this.addClientServiceData = {
          isSimServiceAddition: true,
          clientId: item.client_id,
          clientName: item.client_name,
          imsiId: item.imsi_id,
          imsiNumber: item.imsi_number || item.serial,
          msisdnId: item.msisdn_id,
          msisdnNumber: item.msisdn,
          availableServices: imsiSubscriptionServices,
          existingServices: existingServices
        }

        this.addClientServiceDialog = true
      } catch (err) {
        log('Error opening add client service dialog:', err)
        this.showSnackbar('Eroare la deschiderea dialogului', 'error')
      } finally {
        this.addClientServiceLoading = false
      }
    },

    async onClientServiceAdded() {
      this.addClientServiceDialog = false
      this.addClientServiceData = null
      this.showSnackbar('Serviciu adaugat cu succes', 'success')
      await this.fetchSims()
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },

    async rebuildCache() {
      this.cacheRebuilding = true
      try {
        const { rebuildHierarchyCache } = await import('@/composables/useHierarchyCacheAPI')
        const result = await rebuildHierarchyCache(this.token)
        if (!result.error) {
          this.showSnackbar('Cache rebuild started successfully', 'success')
        } else {
          this.showSnackbar(result.message || 'Error rebuilding cache', 'error')
        }
      } catch (e) {
        this.showSnackbar('Error rebuilding cache', 'error')
      } finally {
        this.cacheRebuilding = false
      }
    },
  }
}
</script>

<style scoped>
.clickable-link {
  color: #1976D2;
  cursor: pointer;
  text-decoration: underline;
}

.clickable-link:hover {
  color: #1565C0;
}

.text-red {
  color: #D32F2F;
  font-weight: bold;
}
</style>
